// /opt/isp_erp/frontend/src/pages/ftth/NapsList.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Grid,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  SignalCellularAlt as SignalIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ftthAPI } from '../../api/ftth.api';
import { useNavigate } from 'react-router-dom';

const NapsList = () => {
  const [naps, setNaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // --- Diálogo ver clientes ---
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNap, setSelectedNap] = useState(null);
  const [clientesNap, setClientesNap] = useState({ clientes: [], total_clientes: 0, nap: null });
  const [loadingClientes, setLoadingClientes] = useState(false);

  // --- Eliminar ---
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // --- Toast ---
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const showToast = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  useEffect(() => {
    cargarNaps();
  }, []);

  const cargarNaps = async () => {
    setLoading(true);
    try {
      const data = await ftthAPI.getNaps();
      setNaps(data);
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar NAPs');
    } finally {
      setLoading(false);
    }
  };

  const verClientesNap = async (nap) => {
    setSelectedNap(nap);
    setOpenDialog(true);
    setLoadingClientes(true);
    try {
      const data = await ftthAPI.getClientesByNap(nap.id);
      setClientesNap(data);
    } catch (error) {
      console.error('Error cargando clientes del NAP:', error);
      setClientesNap({ clientes: [], total_clientes: 0, nap: null });
    } finally {
      setLoadingClientes(false);
    }
  };

  // --- Editar ---
  const handleEditar = (nap) => {
    navigate(`/ftth/naps/${nap.id}/editar`);
  };

  // --- Eliminar ---
  const handleEliminar = (nap) => setConfirmDelete(nap);

  const confirmarEliminar = async (force = false) => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await ftthAPI.deleteNap(confirmDelete.id, force);
      showToast(`NAP ${confirmDelete.codigo} eliminado`, 'success');
      setConfirmDelete(null);
      await cargarNaps();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al eliminar';
      showToast(detail, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'ACTIVO': return 'success';
      case 'LLENO': return 'warning';
      case 'MANTENIMIENTO': return 'info';
      case 'INACTIVO': return 'error';
      default: return 'default';
    }
  };

  const getOcupacionColor = (porcentaje) => {
    if (porcentaje >= 90) return '#f44336';
    if (porcentaje >= 70) return '#ff9800';
    return '#4caf50';
  };

  const napConPuertos = confirmDelete && confirmDelete.puertos_ocupados > 0;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">📋 Lista de NAPs</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/ftth/naps/nuevo')}
        >
          Agregar NAP
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell>Zona/Sector</TableCell>
                <TableCell>Puertos</TableCell>
                <TableCell>Ocupación</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Clientes</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {naps.map((nap) => (
                <TableRow key={nap.id} hover>
                  <TableCell><strong>{nap.codigo}</strong></TableCell>
                  <TableCell>{nap.nombre}</TableCell>
                  <TableCell>{nap.direccion}</TableCell>
                  <TableCell>{nap.zona} / {nap.sector}</TableCell>
                  <TableCell>{nap.puertos_ocupados}/{nap.capacidad_puertos}</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress
                        variant="determinate"
                        value={nap.porcentaje_ocupacion}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 3,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': { bgcolor: getOcupacionColor(nap.porcentaje_ocupacion) }
                        }}
                      />
                      <Typography variant="caption">{nap.porcentaje_ocupacion}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={nap.estado} color={getEstadoColor(nap.estado)} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver clientes conectados">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => verClientesNap(nap)}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        {nap.puertos_ocupados}
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar NAP">
                      <IconButton size="small" color="primary" onClick={() => handleEditar(nap)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar NAP">
                      <IconButton size="small" color="error" onClick={() => handleEliminar(nap)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {naps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay NAPs registrados</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo clientes del NAP */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" component="div">
                📡 Clientes conectados a {selectedNap?.codigo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNap?.nombre} - {selectedNap?.direccion}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenDialog(false)} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {loadingClientes ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : clientesNap.clientes?.length > 0 ? (
            <>
              <Box mb={2} p={2} sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Total clientes:</Typography>
                    <Typography variant="h6" color="primary">{clientesNap.total_clientes}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Ocupación:</Typography>
                    <Typography variant="h6">
                      {clientesNap.nap?.puertos_ocupados}/{clientesNap.nap?.capacidad_puertos}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <List>
                {clientesNap.clientes.map((cliente, index) => (
                  <React.Fragment key={cliente.id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#3b82f6' }}><PersonIcon /></Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle1" component="span">
                              {cliente.nombre_completo}
                            </Typography>
                            <Chip label={`Puerto ${cliente.puerto}`} size="small" color="primary" sx={{ ml: 1 }} />
                          </Box>
                        }
                        secondary={
                          <Grid container spacing={1} mt={0.5}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                📄 ID: {cliente.identificacion}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                📞 Tel: {cliente.telefono || 'No registrado'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                📍 {cliente.direccion || 'Sin dirección'}
                              </Typography>
                            </Grid>
                            {cliente.nivel_senal_rx && (
                              <Grid item xs={12}>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  <SignalIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                                  Nivel de señal: {cliente.nivel_senal_rx} dBm
                                </Typography>
                              </Grid>
                            )}
                            {cliente.fecha_asignacion && (
                              <Grid item xs={12}>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  📅 Asignado: {new Date(cliente.fecha_asignacion).toLocaleDateString('es-ES')}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        }
                      />
                    </ListItem>
                    {index < clientesNap.clientes.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                📭 No hay clientes conectados a este NAP
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo eliminar NAP */}
      <Dialog open={!!confirmDelete} onClose={() => !deleting && setConfirmDelete(null)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {napConPuertos ? <WarningIcon color="warning" /> : <DeleteIcon color="error" />}
          {napConPuertos ? '¿Forzar eliminación?' : '¿Eliminar NAP?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a eliminar <b>{confirmDelete?.codigo}</b> ({confirmDelete?.nombre}).
            <br /><br />
            {napConPuertos ? (
              <>
                ⚠️ Este NAP tiene <b>{confirmDelete.puertos_ocupados} puerto(s) ocupado(s)</b>.
                <br />
                Al forzar la eliminación, esos puertos se liberarán y <b>se perderá el vínculo con los clientes</b>.
                <br /><br />
                ¿Estás seguro?
              </>
            ) : (
              'Esta acción no se puede deshacer.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancelar</Button>
          {napConPuertos ? (
            <Button
              onClick={() => confirmarEliminar(true)}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Eliminando...' : 'Forzar eliminación'}
            </Button>
          ) : (
            <Button
              onClick={() => confirmarEliminar(false)}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.sev} onClose={() => setToast({ ...toast, open: false })}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NapsList;
